export const AI_SURVEY_ID = "ai-digital-skills-2026";
export const AI_SURVEY_VERSION = 1;
export const AI_SURVEY_NAME = "Community AI, Coding & Digital Skills Survey";
export const AI_SURVEY_STORAGE_KEY = `uun-community-voice:${AI_SURVEY_ID}`;
export const AI_SURVEY_DRAFT_KEY = `${AI_SURVEY_STORAGE_KEY}:draft`;

export const WORKSHOP_INTEREST = [
  "Yes, definitely",
  "Yes, possibly",
  "Not sure",
  "Not at the moment",
  "I am interested for my child/young person",
];

export const WORKSHOP_TOPICS = [
  "Introduction to AI",
  "ChatGPT and other AI tools",
  "AI for work and productivity",
  "AI for finding jobs, writing CVs and preparing for interviews",
  "AI for small businesses and self-employment",
  "Basic computer and internet skills",
  "Introduction to coding",
  "Website development",
  "App development",
  "Game development",
  "Digital design/content creation",
  "Cybersecurity and online safety",
  "Coding and technology for children",
  "Parent and child digital workshops",
  "Other",
];

export const DIGITAL_CONFIDENCE = [
  "Not confident",
  "A little confident",
  "Moderately confident",
  "Confident",
  "Very confident",
];

export const AI_EXPERIENCE = [
  "Yes, regularly",
  "Yes, occasionally",
  "I have heard of them but haven't used them",
  "No",
  "Not sure",
];

export const MOTIVATIONS = [
  "Finding employment",
  "Improving my current job skills",
  "Starting or growing a business",
  "Learning something new",
  "Supporting my children",
  "Building a website or app",
  "Exploring a career in technology",
  "Improving my digital confidence",
  "Using AI safely",
  "Community or voluntary work",
  "Other",
];

export const DELIVERY_PREFERENCES = [
  "Phoenix Heights Community Centre",
  "Another local community venue",
  "Online",
  "Combination of online and in-person",
  "Short workshops",
  "Multi-week course",
  "Drop-in sessions",
  "Family sessions",
  "No preference",
];

export const PREFERRED_TIMES = [
  "Weekday mornings",
  "Weekday afternoons",
  "Weekday evenings",
  "Saturday",
  "Sunday",
  "School holidays",
  "No preference",
];

export const BARRIERS = [
  "Cost",
  "Childcare",
  "Caring responsibilities",
  "Work commitments",
  "Lack of confidence",
  "Language",
  "Disability/access requirements",
  "Not having a computer",
  "Internet access",
  "Course times",
  "Nothing would prevent me attending",
  "Other",
];

export const DEVICE_ACCESS = [
  "Yes",
  "I mainly use a smartphone/tablet",
  "I share a computer",
  "No",
  "Prefer not to say",
];

export const AI_SURVEY_AREAS = [
  "Isle of Dogs",
  "Canary Wharf",
  "Poplar",
  "Limehouse",
  "Bow",
  "Stepney",
  "Shadwell",
  "Whitechapel",
  "Other Tower Hamlets",
  "Outside Tower Hamlets",
  "Prefer not to say",
];

export const RIVERSIDE_OPTIONS = ["Yes", "No", "Not sure", "Prefer not to say"];

export const AI_SURVEY_AGES = [
  "Under 13",
  "13–15",
  "16–17",
  "18–24",
  "25–34",
  "35–44",
  "45–54",
  "55–64",
  "65+",
  "Prefer not to say",
];

export const YOUTH_INTEREST = ["Yes", "Maybe", "No", "Not applicable"];
export const CONTACT_PERMISSION = ["Yes", "No"];
export const CONTACT_METHODS = ["Email", "Mobile", "Either"];
export const REFERRAL_SOURCES = [
  "WhatsApp",
  "QR code or poster",
  "UUN website",
  "Social media",
  "Riverside or another partner",
  "Friend or family",
  "Other",
  "Prefer not to say",
];

export const AI_SURVEY_STEPS = [
  { title: "Your interests", fields: ["workshopInterest", "topics", "topicsOther", "priorityTopic"] },
  { title: "Skills and goals", fields: ["digitalConfidence", "aiExperience", "motivations", "motivationsOther"] },
  { title: "Making workshops work", fields: ["deliveryPreference", "preferredTimes", "barriers", "barriersOther", "deviceAccess"] },
  { title: "About your area", fields: ["area", "riversideResident", "ageGroup", "youthInterest"] },
  { title: "Keep in touch", fields: ["contactPermission", "contactName", "contactEmail", "contactMobile", "preferredContactMethod", "contactConsent", "finalComments", "referralSource", "referralSourceOther"] },
];

export const emptyAiSurveyAnswers = {
  workshopInterest: "",
  topics: [],
  topicsOther: "",
  priorityTopic: "",
  digitalConfidence: "",
  aiExperience: "",
  motivations: [],
  motivationsOther: "",
  deliveryPreference: [],
  preferredTimes: [],
  barriers: [],
  barriersOther: "",
  deviceAccess: "",
  area: "",
  riversideResident: "",
  ageGroup: "",
  youthInterest: "",
  contactPermission: "",
  contactName: "",
  contactEmail: "",
  contactMobile: "",
  preferredContactMethod: "",
  contactConsent: false,
  finalComments: "",
  referralSource: "",
  referralSourceOther: "",
};

export function getPriorityOptions(values) {
  return values.topics.map((topic) => {
    if (topic !== "Other") return topic;
    return values.topicsOther.trim() || "Other";
  });
}

export function validateAiDigitalSkillsSurvey(values, stepIndex = null) {
  const errors = {};
  const required = (key, message) => {
    if (!values[key]) errors[key] = message;
  };
  const requireList = (key, message) => {
    if (!values[key]?.length) errors[key] = message;
  };

  required("workshopInterest", "Choose how interested you are in attending.");
  requireList("topics", "Choose at least one workshop topic.");
  if (values.topics.includes("Other") && !values.topicsOther.trim()) {
    errors.topicsOther = "Tell us which other workshop interests you.";
  }
  required("priorityTopic", "Choose the workshop you would most like us to run first.");
  if (values.priorityTopic && !getPriorityOptions(values).includes(values.priorityTopic)) {
    errors.priorityTopic = "Choose a priority from your selected workshop topics.";
  }
  required("digitalConfidence", "Choose your current level of digital confidence.");
  required("aiExperience", "Tell us about your experience with AI tools.");
  requireList("motivations", "Choose at least one reason for learning these skills.");
  if (values.motivations.includes("Other") && !values.motivationsOther.trim()) {
    errors.motivationsOther = "Tell us what else you would like these skills to help with.";
  }
  requireList("deliveryPreference", "Choose at least one preferred workshop format.");
  requireList("preferredTimes", "Choose at least one time when you could attend.");
  requireList("barriers", "Choose at least one answer about barriers to attending.");
  if (values.barriers.includes("Other") && !values.barriersOther.trim()) {
    errors.barriersOther = "Tell us about the other barrier.";
  }
  required("deviceAccess", "Choose the answer that best describes your device access.");
  required("area", "Choose the area where you live.");
  required("riversideResident", "Tell us whether you are a Riverside Housing resident.");
  required("ageGroup", "Choose your age group.");
  required("youthInterest", "Tell us whether a child or young person may be interested.");
  required("contactPermission", "Choose whether you would like workshop updates.");

  if (values.contactPermission === "Yes") {
    if (!values.contactName.trim()) errors.contactName = "Enter your name.";
    if (!values.contactEmail.trim() && !values.contactMobile.trim()) {
      errors.contactEmail = "Enter an email address or mobile number.";
      errors.contactMobile = "Enter an email address or mobile number.";
    }
    if (values.contactEmail && !/^\S+@\S+\.\S+$/.test(values.contactEmail.trim())) {
      errors.contactEmail = "Enter a valid email address.";
    }
    required("preferredContactMethod", "Choose how you would prefer UUN to contact you.");
    if (values.preferredContactMethod === "Email" && !values.contactEmail.trim()) {
      errors.contactEmail = "Enter an email address for email updates.";
    }
    if (values.preferredContactMethod === "Mobile" && !values.contactMobile.trim()) {
      errors.contactMobile = "Enter a mobile number for mobile updates.";
    }
    if (!values.contactConsent) errors.contactConsent = "Confirm that UUN may contact you about these workshops.";
  }

  if (values.finalComments.length > 1000) errors.finalComments = "Keep your answer to 1,000 characters.";
  if (values.referralSource === "Other" && !values.referralSourceOther.trim()) {
    errors.referralSourceOther = "Tell us how you heard about the survey.";
  }

  if (stepIndex === null) return errors;
  const fields = AI_SURVEY_STEPS[stepIndex]?.fields || [];
  return Object.fromEntries(Object.entries(errors).filter(([key]) => fields.includes(key)));
}
